import React, {useEffect, useState} from 'react';

import {IconButton} from '@albert-io/atomic';
import {LtiSyncModal} from 'client/components/ContentDiscovery/Document/AssignmentSharing/LtiSyncModal';
import {useLtiSync} from 'client/components/ContentDiscovery/Document/AssignmentSharing/useLtiSync';
import {
  getLtiPlatformData,
  withStopPropagation
} from 'client/components/ContentDiscovery/Document/AssignmentSharing/utils';

import {CantSyncInfoPopover} from './CantSyncInfoPopover';
import {LMS_SYNC_ICON_BUTTON_PROPS} from './lmsSyncConfig';
import {SyncStatusOverlay} from './SyncStatusOverlay';

const RESYNC_CLEAR_MS = 3000;

interface Props {
  assignmentId: string;
  classroomId: string;
  schoolId: string | null;
  /** Raw Mandark platform key, for the help link + confirm-modal copy. */
  ltiPlatformName: string;
  /** Class is connected to an LMS course; false → render the can't-sync popover. */
  classroomConnectedToLti: boolean;
  /** Seeds the initial synced state (already has a classroom-scoped line item). */
  initialSynced: boolean;
}

/**
 * Per-assignment LMS grade-sync affordance for the v2 (class-scoped) assignments row — the Figma's
 * stateful sync icon (can't-sync / error / synced / sync), reusing the legacy `useLtiSync` hook +
 * confirm modal so the sync flow itself is unchanged.
 */
export const AssignmentLtiSyncCell = ({
  assignmentId,
  classroomId,
  schoolId,
  ltiPlatformName,
  classroomConnectedToLti,
  initialSynced
}: Props) => {
  const [showSyncModal, setShowSyncModal] = useState(false);
  const {
    hasSyncedWithLti,
    resyncCompleted,
    setResyncCompleted,
    isRequestPending,
    syncError,
    syncLti,
    resyncLti
  } = useLtiSync(assignmentId, classroomId, schoolId, initialSynced);
  const {ltiPlatformDisplayName, ltiPlatformHelpLink} = getLtiPlatformData(ltiPlatformName);

  // Re-enable the re-sync button a few seconds after a completed re-sync.
  useEffect(() => {
    if (!resyncCompleted) {
      return undefined;
    }
    const timer = setTimeout(() => setResyncCompleted(false), RESYNC_CLEAR_MS);
    return () => clearTimeout(timer);
  }, [resyncCompleted, setResyncCompleted]);

  let iconButton;
  if (!classroomConnectedToLti) {
    // Class not connected → an interactive-hover popover (so the help link is actually reachable) over
    // the muted slashed-sync glyph.
    iconButton = <CantSyncInfoPopover ltiPlatformHelpLink={ltiPlatformHelpLink} />;
  } else if (syncError) {
    // Failed sync/re-sync → error glyph + retry (the hook already raised the toast).
    iconButton = (
      <IconButton
        {...LMS_SYNC_ICON_BUTTON_PROPS}
        label='Retry grade sync'
        className='relative'
        disabled={isRequestPending}
        onClick={withStopPropagation(syncLti)}
        tooltip='Grade sync failed — click to retry'
        iconProps={{color: 'tertiary', spin: isRequestPending}}
      >
        <SyncStatusOverlay status='error' />
      </IconButton>
    );
  } else if (hasSyncedWithLti) {
    iconButton = (
      <IconButton
        {...LMS_SYNC_ICON_BUTTON_PROPS}
        label='Synced with LMS'
        className='relative'
        disabled={isRequestPending || resyncCompleted}
        onClick={withStopPropagation(resyncLti)}
        tooltip='Synced with LMS'
        iconProps={{spin: isRequestPending}}
      >
        <SyncStatusOverlay status='synced' />
      </IconButton>
    );
  } else {
    iconButton = (
      <IconButton
        {...LMS_SYNC_ICON_BUTTON_PROPS}
        label='Sync grades'
        disabled={isRequestPending}
        onClick={withStopPropagation(() => setShowSyncModal(true))}
        tooltip='Sync grades'
        iconProps={{spin: isRequestPending}}
      />
    );
  }

  // Render the modal independent of the icon branch, so a sync failure (→ error glyph) doesn't unmount
  // the open confirm dialog mid-flow.
  return (
    <>
      {iconButton}
      {showSyncModal && (
        <LtiSyncModal
          ltiPlatformName={ltiPlatformDisplayName}
          syncLti={syncLti}
          isRequestPending={isRequestPending}
          onDismiss={() => setShowSyncModal(false)}
        />
      )}
    </>
  );
};

/**
 * Cross-class (All Classwork) variant: sync is per-class only, so this is a non-actionable sync icon
 * whose tooltip points the teacher to the per-class flow. Shown only when ≥1 class has an LTI
 * deployment.
 */
export const CrossClassLtiSyncCell = () => (
  // Informational only — rendered as a labelled `img` (not a button) so it's out of the tab order and
  // AT announces the guidance instead of a no-op command. Sync is per-class.
  <IconButton
    {...LMS_SYNC_ICON_BUTTON_PROPS}
    as='span'
    role='img'
    label='Grade sync is available per class'
    tooltip='To sync grades, navigate to an individual class or open the assignment editor.'
  />
);
